/**
 * Email Variable System - Shared Utilities
 * 
 * Central source of truth for all email quick tags and variable resolution.
 * Used across all email modals (EmailCoach, BulkEmail, ThreadReply).
 * 
 * Key Concepts:
 * - QuickTags: UI elements that users click to insert variables
 * - Placeholders: Template strings like {{athlete.firstName}} used in Email/Bulk modals
 * - Direct Values: Resolved values like "John" used in ThreadReply modals
 * - Resolution: Converting placeholders to actual values at send time
 */

import { fieldLabel } from './fieldLabel';

// ==================== Type Definitions ====================

export interface QuickTag {
  label: string;
  value: string;
  color: string;
  bgColor: string;
  category?: string;
}

/**
 * One of the athlete's own games at the event a message is about (KGH-795 /
 * APS-255 item 11). Structurally a subset of TournamentGame, so a caller can
 * hand its tournament.games straight in.
 */
export interface EventScheduleGame {
  game_date: string;
  game_time?: string | null;
  opponent?: string | null;
  field_number?: string | null;
}

export interface EmailVariableContext {
  hasCoach: boolean;
  hasYouTube: boolean;
  isThreadReply: boolean;
  showAllTags?: boolean;
  athlete?: any;
  coach?: any;
  sportInfo?: SportInfo | null;
  academicData?: AcademicData | null;
  performanceMetrics?: PerformanceMetrics | null;
  schoolInfo?: SchoolInfo | null;
  staffData?: StaffData | null;
  profileShareUrl?: string;
  /**
   * The athlete's games at the ONE event this composer was opened about. Only
   * the surface that knows which event is being discussed can supply it, so it
   * travels with the rest of the context rather than being read from anywhere
   * global — a composer opened from anywhere else simply has none, and the tag
   * is neither offered nor resolvable there.
   */
  eventSchedule?: EventScheduleGame[] | null;
}

export interface AthleteData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  graduationYear?: number;
  sport?: string;
  position?: string;
  height?: string | number;
  weight?: number;
  youtube_channel_url?: string;
  youtube_channel_handle?: string;
  /** APS-259. The highlight video link (YouTube). */
  featured_video_url?: string | null;
}

export interface CoachData {
  coach_name?: string;
  name?: string;
  title?: string;
  school?: string;
  email?: string;
}

export interface SportInfo {
  sport?: string;
  positions?: string[];
  bats?: string;
  throws?: string;
  height?: string | number;
  weight?: number;
}

export interface AcademicData {
  currentGpa?: string;
  gpaScale?: string;
  satTotal?: string;
  satMath?: string;
  satReadingWriting?: string;
  actTotal?: string;
  psatTotal?: string;
  psatMath?: string;
  psatReadingWriting?: string;
}

export interface PerformanceMetrics {
  sixty_yard_time?: number | null;
  forty_yard_time?: number | null;
  twenty_yard_time?: number | null;
  ten_yard_time?: number | null;
  home_to_first?: number | null;
  pop_time?: number | null;
  exit_velocity?: number | null;
  overhand_velocity?: number | null;
  grip_strength?: number | null;
}

export interface SchoolInfo {
  school?: {
    name?: string;
  };
  travelClub?: {
    name?: string;
  };
}

export interface StaffData {
  name?: string;
  orgName?: string;
  teamName?: string;
}

export interface ResolveData {
  athlete?: any;
  coach?: any;
  sportInfo?: SportInfo | null;
  academicData?: AcademicData | null;
  performanceMetrics?: PerformanceMetrics | null;
  schoolInfo?: SchoolInfo | null;
  staffData?: StaffData | null;
  profileShareUrl?: string;
  eventSchedule?: EventScheduleGame[] | null;
}

// ==================== Helper Formatters ====================

/**
 * Format coach name as "Coach LastName" or "Coach FirstName"
 */
export const formatCoachName = (fullName?: string): string => {
  if (!fullName || fullName.trim() === '') {
    return 'Coach';
  }

  const nameParts = fullName.trim().split(' ');
  if (nameParts.length >= 2) {
    const lastName = nameParts[nameParts.length - 1];
    return `Coach ${lastName}`;
  }

  return `Coach ${nameParts[0]}`;
};

/**
 * Format height from inches to feet'inches" format
 */
export const formatHeight = (height?: string | number): string => {
  if (!height) return '';

  if (typeof height === 'number') {
    const feet = Math.floor(height / 12);
    const inches = height % 12;
    return `${feet}'${inches}"`;
  }

  if (typeof height === 'string') {
    const num = parseInt(height);
    if (!isNaN(num)) {
      const feet = Math.floor(num / 12);
      const inches = num % 12;
      return `${feet}'${inches}"`;
    }
    return height;
  }

  return '';
};

/**
 * Format time values with 's' suffix
 */
export const formatTime = (time?: number | null): string => {
  if (!time || time === 0) return '';
  return `${time}s`;
};

/**
 * Format velocity values with 'mph' suffix
 */
export const formatVelocity = (velocity?: number | null): string => {
  if (!velocity || velocity === 0) return '';
  return `${velocity} mph`;
};

/**
 * Format strength values with 'lbs' suffix
 */
export const formatStrength = (strength?: number | null): string => {
  if (!strength || strength === 0) return '';
  return `${strength} lbs`;
};

/**
 * Format weight with 'lbs' suffix
 */
export const formatWeight = (weight?: number): string => {
  if (!weight || weight === 0) return '';
  return `${weight} lbs`;
};

/**
 * Format GPA with scale
 */
export const formatGPA = (gpa?: string, scale?: string): string => {
  if (!gpa) return '';
  if (!scale) return gpa;
  return `${gpa} / ${scale}`;
};

/**
 * Format array of strings with proper grammar
 */
export const formatArray = (arr?: string[]): string => {
  if (!arr || arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return arr.join(' and ');
  return arr.slice(0, -1).join(', ') + ', and ' + arr[arr.length - 1];
};

/**
 * Format positions array
 */
export const formatPositions = (positions?: string[]): string => {
  return formatArray(positions);
};

/**
 * Format hometown from city and state
 */
export const formatHometown = (city?: string, state?: string): string => {
  if (!city && !state) return '';
  if (city && state) return `${city}, ${state}`;
  return city || state || '';
};

/**
 * Format one game's start time ("14:30:00" -> "2:30 PM"). Anything unparseable
 * comes back empty rather than as a raw DB value in front of a coach.
 */
const formatGameTime = (time?: string | null): string => {
  const raw = String(time || '').trim();
  if (!raw) return '';
  const parsed = new Date(`2000-01-01T${raw}`);
  if (isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * The athlete's games at one event, as a short block a coach can read in the
 * body of an email — one line per game, in the order they will be played:
 *
 *   Sat, Jul 11, 8:00 AM - Field 3 - vs Dallas Tigers
 *   Sat, Jul 11, time TBD - vs Stars 16U
 *
 * Deliberately not a table: this lands in a plain-text Gmail body, where the
 * columns of a table collapse and a coach reading it on a phone at a field gets
 * a wall of pipes. A missing time says so out loud ("time TBD") because a coach
 * planning which field to stand on needs to know the difference between "early"
 * and "not decided"; a missing field or opponent simply drops out of the line.
 *
 * Dates are parsed at local midnight (the tournament modal does the same) so a
 * DATE column doesn't slide a day backwards for anyone west of UTC.
 */
export const formatEventSchedule = (games?: EventScheduleGame[] | null): string => {
  const dayOf = (g: EventScheduleGame) => String(g.game_date || '').split('T')[0];
  const playable = (games || []).filter((g) => g && dayOf(g));
  if (playable.length === 0) return '';

  // A game with no time goes last within its day — it is the least useful line
  // to a coach, and sorting '' first would push it above a known 8:00 AM.
  const sorted = [...playable].sort((a, b) => {
    const byDay = dayOf(a).localeCompare(dayOf(b));
    if (byDay !== 0) return byDay;
    return String(a.game_time || '99:99:99').localeCompare(String(b.game_time || '99:99:99'));
  });

  return sorted
    .map((g) => {
      const day = dayOf(g);
      const parsedDay = new Date(`${day}T00:00:00`);
      const dayLabel = isNaN(parsedDay.getTime())
        ? day
        : parsedDay.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const time = formatGameTime(g.game_time);
      const field = fieldLabel(g.field_number);
      const opponent = String(g.opponent || '').trim();
      return [
        `${dayLabel}, ${time || 'time TBD'}`,
        field || '',
        opponent ? `vs ${opponent}` : '',
      ]
        .filter(Boolean)
        .join(' - ');
    })
    .join('\n');
};

/**
 * Get position by index (0, 1, 2)
 */
export const getPosition = (positions?: string[], index: number = 0): string => {
  if (!positions || positions.length === 0) return '';
  if (index >= positions.length) return '';
  return positions[index];
};

// ==================== Quick Tags Generation ====================

/**
 * Generate quick tags based on context
 * 
 * @param context - Configuration for which tags to include
 * @returns Array of QuickTag objects for UI display
 */
/**
 * APS-259. The first draft in the coach email composer. The video and
 * profile link lines go in only when their values exist.
 */
export const coachEmailTemplateBody = (opts: { hasVideo: boolean; hasProfileLink: boolean }): string => {
  const linkLines = [
    opts.hasVideo ? 'Highlight video: {{athlete.featuredVideo}}' : null,
    opts.hasProfileLink ? 'My APS profile: {{athlete.profileShareLink}}' : null,
  ].filter(Boolean);
  return (
    `{{coach.name}},\n\n` +
    `My name is {{athlete.name}}. I am a {{athlete.graduationYear}} {{athlete.position}} who currently plays for {{athlete.travelClubName}} and attends {{athlete.schoolName}}. I'm interested in attending {{coach.school}} and wanted to get on your radar.\n\n` +
    (linkLines.length ? `${linkLines.join('\n')}\n\n` : '') +
    `\n\n` +
    `Best regards,\n\n` +
    `{{athlete.name}}\n` +
    `Class of {{athlete.graduationYear}}\n` +
    `GPA: {{athlete.gpa}}\n` +
    `{{athlete.email}}\n` +
    `{{athlete.phone}}`
  );
};

export const getQuickTags = (context: EmailVariableContext): QuickTag[] => {
  const { hasCoach, hasYouTube, isThreadReply, showAllTags, athlete, coach, sportInfo, academicData, performanceMetrics, schoolInfo, staffData, profileShareUrl, eventSchedule } = context;
  
  const tags: QuickTag[] = [];

  // ===== ATHLETE BASIC INFO — filtered by data availability (or show all for templates) =====
  // Name tags are always shown (guaranteed to exist)
  tags.push({ label: 'Athlete Name', value: '{{athlete.name}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  tags.push({ label: 'First Name', value: '{{athlete.firstName}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  tags.push({ label: 'Last Name', value: '{{athlete.lastName}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  if (showAllTags || athlete?.email) tags.push({ label: 'Email', value: '{{athlete.email}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  if (showAllTags || athlete?.phone) tags.push({ label: 'Phone', value: '{{athlete.phone}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  if (showAllTags || athlete?.city) tags.push({ label: 'City', value: '{{athlete.city}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  if (showAllTags || athlete?.state) tags.push({ label: 'State', value: '{{athlete.state}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  if (showAllTags || athlete?.city || athlete?.state) tags.push({ label: 'Hometown', value: '{{athlete.hometown}}', color: '#10B981', bgColor: '#D1FAE5', category: 'Athlete Info' });
  if (showAllTags || athlete?.graduationYear) tags.push({ label: 'Graduation Year', value: '{{athlete.graduationYear}}', color: '#6366F1', bgColor: '#E0E7FF', category: 'Athlete Info' });
  if (showAllTags || schoolInfo?.school?.name) tags.push({ label: 'High School Name', value: '{{athlete.schoolName}}', color: '#7C2D12', bgColor: '#FEF7F3', category: 'Athlete Info' });
  if (showAllTags || schoolInfo?.travelClub?.name) tags.push({ label: 'Travel Club Team Name', value: '{{athlete.travelClubName}}', color: '#7C2D12', bgColor: '#FEF7F3', category: 'Athlete Info' });

  // ===== SPORT INFO — filtered by data availability =====
  if (showAllTags || sportInfo?.sport || athlete?.sport) tags.push({ label: 'Sport', value: '{{athlete.sport}}', color: '#8B5CF6', bgColor: '#EDE9FE', category: 'Sport Info' });
  if (showAllTags || sportInfo?.positions?.[0]) {
    tags.push({ label: 'Primary Position', value: '{{athlete.position}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
    tags.push({ label: 'Position 1', value: '{{athlete.position1}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  }
  if (showAllTags || sportInfo?.positions?.[1]) tags.push({ label: 'Position 2', value: '{{athlete.position2}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  if (showAllTags || sportInfo?.positions?.[2]) tags.push({ label: 'Position 3', value: '{{athlete.position3}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  if (showAllTags || (sportInfo?.positions && sportInfo.positions.length > 0)) tags.push({ label: 'All Positions', value: '{{athlete.positions}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  if (showAllTags || sportInfo?.bats) tags.push({ label: 'Bats', value: '{{athlete.bats}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  if (showAllTags || sportInfo?.throws) tags.push({ label: 'Throws', value: '{{athlete.throws}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  if (showAllTags || sportInfo?.height) tags.push({ label: 'Height', value: '{{athlete.height}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });
  if (showAllTags || sportInfo?.weight) tags.push({ label: 'Weight', value: '{{athlete.weight}}', color: '#F59E0B', bgColor: '#FEF3C7', category: 'Sport Info' });

  // ===== ACADEMIC — filtered by data availability =====
  if (showAllTags || academicData?.currentGpa) tags.push({ label: 'GPA', value: '{{athlete.gpa}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.gpaScale) tags.push({ label: 'GPA Scale', value: '{{athlete.gpaScale}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.satTotal) tags.push({ label: 'SAT Total', value: '{{athlete.satTotal}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.satMath) tags.push({ label: 'SAT Math', value: '{{athlete.satMath}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.satReadingWriting) tags.push({ label: 'SAT Reading', value: '{{athlete.satReading}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.actTotal) tags.push({ label: 'ACT Total', value: '{{athlete.actTotal}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.psatTotal) tags.push({ label: 'PSAT Total', value: '{{athlete.psatTotal}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.psatMath) tags.push({ label: 'PSAT Math', value: '{{athlete.psatMath}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });
  if (showAllTags || academicData?.psatReadingWriting) tags.push({ label: 'PSAT Reading', value: '{{athlete.psatReading}}', color: '#DC2626', bgColor: '#FEE2E2', category: 'Academic' });

  // ===== PERFORMANCE METRICS — filtered by data availability =====
  if (showAllTags || performanceMetrics?.sixty_yard_time) tags.push({ label: '5x10x5 Shuttle', value: '{{athlete.sixtyYardTime}}', color: '#059669', bgColor: '#ECFDF5', category: 'Performance' });
  if (showAllTags || performanceMetrics?.forty_yard_time) tags.push({ label: '40 Yard Time', value: '{{athlete.fortyYardTime}}', color: '#059669', bgColor: '#ECFDF5', category: 'Performance' });
  if (showAllTags || performanceMetrics?.twenty_yard_time) tags.push({ label: '20 Yard Time', value: '{{athlete.twentyYardTime}}', color: '#059669', bgColor: '#ECFDF5', category: 'Performance' });
  if (showAllTags || performanceMetrics?.ten_yard_time) tags.push({ label: '10 Yard Time', value: '{{athlete.tenYardTime}}', color: '#059669', bgColor: '#ECFDF5', category: 'Performance' });
  if (showAllTags || performanceMetrics?.home_to_first) tags.push({ label: 'Home to 1st', value: '{{athlete.homeToFirst}}', color: '#059669', bgColor: '#ECFDF5', category: 'Performance' });
  if (showAllTags || performanceMetrics?.pop_time) tags.push({ label: 'Pop Time', value: '{{athlete.popTime}}', color: '#059669', bgColor: '#ECFDF5', category: 'Performance' });
  if (showAllTags || performanceMetrics?.exit_velocity) tags.push({ label: 'Exit Velocity', value: '{{athlete.exitVelocity}}', color: '#0891B2', bgColor: '#CFFAFE', category: 'Performance' });
  if (showAllTags || performanceMetrics?.overhand_velocity) tags.push({ label: 'Overhand Velocity', value: '{{athlete.overhandVelocity}}', color: '#0891B2', bgColor: '#CFFAFE', category: 'Performance' });
  if (showAllTags || performanceMetrics?.grip_strength) tags.push({ label: 'Grip Strength', value: '{{athlete.gripStrength}}', color: '#7C2D12', bgColor: '#FEF7F3', category: 'Performance' });

  // ===== YOUTUBE INTEGRATION (2 variables) =====
  if (showAllTags || hasYouTube) {
    const youtubeTags: QuickTag[] = [
      { label: 'YouTube Channel URL', value: '{{athlete.youtubeChannelUrl}}', color: '#FF0000', bgColor: '#FFEBEE', category: 'YouTube' },
      { label: 'YouTube Handle', value: '{{athlete.youtubeChannelHandle}}', color: '#FF0000', bgColor: '#FFEBEE', category: 'YouTube' },
    ];
    tags.push(...youtubeTags);
  }

  // ===== COACH VARIABLES (4 variables) - Only for Email/Bulk, not ThreadReply =====
  if ((showAllTags || hasCoach) && !isThreadReply) {
    const coachTags: QuickTag[] = [
      { label: 'Coach Name', value: '{{coach.name}}', color: '#EF4444', bgColor: '#FEE2E2', category: 'Coach Info' },
      { label: 'Coach Title', value: '{{coach.title}}', color: '#EF4444', bgColor: '#FEE2E2', category: 'Coach Info' },
      { label: 'School Name', value: '{{coach.school}}', color: '#3B82F6', bgColor: '#DBEAFE', category: 'Coach Info' },
      { label: 'Coach Email', value: '{{coach.email}}', color: '#EF4444', bgColor: '#FEE2E2', category: 'Coach Info' },
    ];
    tags.push(...coachTags);
  }

  // ===== HIGHLIGHT VIDEO (APS-259) =====
  if (showAllTags || athlete?.featured_video_url) {
    tags.push({ label: 'Highlight Video', value: '{{athlete.featuredVideo}}', color: '#6366F1', bgColor: '#E0E7FF', category: 'Athlete Info' });
  }

  // ===== PROFILE SHARE LINK =====
  if (showAllTags || profileShareUrl) {
    tags.push({ label: 'Profile Share Link', value: '{{athlete.profileShareLink}}', color: '#6366F1', bgColor: '#E0E7FF', category: 'Athlete Info' });
  }

  // ===== EVENT SCHEDULE (KGH-795 item 11) — offered only where the composer was
  // opened about a specific event AND the athlete has games at it. Everywhere
  // else there is nothing for it to resolve to, and a tag that always resolves
  // to nothing is worse than no tag.
  if (showAllTags || (eventSchedule && eventSchedule.length > 0)) {
    tags.push({ label: 'Tournament Schedule', value: '{{athlete.tournamentSchedule}}', color: '#6366F1', bgColor: '#E0E7FF', category: 'Athlete Info' });
  }

  // ===== STAFF-SPECIFIC VARIABLES — only show tags the user actually has data for =====
  if (showAllTags || staffData?.name) tags.push({ label: 'Staff Name', value: '{{staff.name}}', color: '#7C3AED', bgColor: '#EDE9FE', category: 'Staff Info' });
  if (showAllTags || staffData?.orgName) tags.push({ label: 'Organization Name', value: '{{staff.orgName}}', color: '#7C3AED', bgColor: '#EDE9FE', category: 'Staff Info' });
  if (showAllTags || staffData?.teamName) tags.push({ label: 'Team Name', value: '{{staff.teamName}}', color: '#7C3AED', bgColor: '#EDE9FE', category: 'Staff Info' });
  if (showAllTags || (staffData && athlete)) {
    tags.push({ label: 'Profile Link', value: '{{athlete.profileLink}}', color: '#7C3AED', bgColor: '#EDE9FE', category: 'Staff Info' });
    if (athlete?.youtube_channel_url) tags.push({ label: 'YouTube Link', value: '{{athlete.youtubeLink}}', color: '#7C3AED', bgColor: '#EDE9FE', category: 'Staff Info' });
    tags.push({ label: 'Athlete Highlight', value: '{{athlete.highlight}}', color: '#7C3AED', bgColor: '#EDE9FE', category: 'Staff Info' });
  }

  // ===== FOR THREAD REPLIES: Resolve values directly =====
  if (isThreadReply && athlete) {
    tags.forEach(tag => {
      tag.value = resolveVariable(tag.value, { athlete, coach, eventSchedule });
    });
  }

  return tags;
};

// ==================== Variable Resolution ====================

/**
 * Resolve a single variable placeholder to its actual value
 */
const resolveVariable = (placeholder: string, data: ResolveData): string => {
  const { athlete, coach, sportInfo, academicData, performanceMetrics, schoolInfo, staffData } = data;

  // Remove {{ and }} from placeholder
  const variable = placeholder.replace('{{', '').replace('}}', '');

  // Coach variables
  if (variable === 'coach.name') return formatCoachName(coach?.coach_name || coach?.name);
  if (variable === 'coach.title') return coach?.title || 'Coach';
  if (variable === 'coach.school') return coach?.school || 'your school';
  if (variable === 'coach.email') return coach?.email || '';

  // Athlete basic info
  if (variable === 'athlete.name') {
    return `${athlete?.firstName || ''} ${athlete?.lastName || ''}`.trim() || '';
  }
  if (variable === 'athlete.firstName') return athlete?.firstName || '';
  if (variable === 'athlete.lastName') return athlete?.lastName || '';
  if (variable === 'athlete.email') return athlete?.email || '';
  if (variable === 'athlete.phone') return athlete?.phone || '';
  if (variable === 'athlete.city') return athlete?.city || '';
  if (variable === 'athlete.state') return athlete?.state || '';
  if (variable === 'athlete.hometown') return formatHometown(athlete?.city, athlete?.state);
  if (variable === 'athlete.graduationYear') return athlete?.graduationYear?.toString() || '';
  if (variable === 'athlete.schoolName') return schoolInfo?.school?.name || '';
  if (variable === 'athlete.travelClubName') return schoolInfo?.travelClub?.name || '';

  // Sport information
  if (variable === 'athlete.sport') return sportInfo?.sport || athlete?.sport || '';
  if (variable === 'athlete.position') return getPosition(sportInfo?.positions, 0);
  if (variable === 'athlete.position1') return getPosition(sportInfo?.positions, 0);
  if (variable === 'athlete.position2') return getPosition(sportInfo?.positions, 1);
  if (variable === 'athlete.position3') return getPosition(sportInfo?.positions, 2);
  if (variable === 'athlete.positions') return formatPositions(sportInfo?.positions);
  if (variable === 'athlete.bats') return sportInfo?.bats || '';
  if (variable === 'athlete.throws') return sportInfo?.throws || '';
  if (variable === 'athlete.height') return formatHeight(sportInfo?.height);
  if (variable === 'athlete.weight') return formatWeight(sportInfo?.weight);

  // Academic information
  if (variable === 'athlete.gpa') return academicData?.currentGpa || '';
  if (variable === 'athlete.gpaScale') return academicData?.gpaScale || '';
  if (variable === 'athlete.satTotal') return academicData?.satTotal || '';
  if (variable === 'athlete.satMath') return academicData?.satMath || '';
  if (variable === 'athlete.satReading') return academicData?.satReadingWriting || '';
  if (variable === 'athlete.actTotal') return academicData?.actTotal || '';
  if (variable === 'athlete.psatTotal') return academicData?.psatTotal || '';
  if (variable === 'athlete.psatMath') return academicData?.psatMath || '';
  if (variable === 'athlete.psatReading') return academicData?.psatReadingWriting || '';

  // Performance Metrics
  if (variable === 'athlete.sixtyYardTime') return formatTime(performanceMetrics?.sixty_yard_time);
  if (variable === 'athlete.fortyYardTime') return formatTime(performanceMetrics?.forty_yard_time);
  if (variable === 'athlete.twentyYardTime') return formatTime(performanceMetrics?.twenty_yard_time);
  if (variable === 'athlete.tenYardTime') return formatTime(performanceMetrics?.ten_yard_time);
  if (variable === 'athlete.homeToFirst') return formatTime(performanceMetrics?.home_to_first);
  if (variable === 'athlete.popTime') return formatTime(performanceMetrics?.pop_time);
  if (variable === 'athlete.exitVelocity') return formatVelocity(performanceMetrics?.exit_velocity);
  if (variable === 'athlete.overhandVelocity') return formatVelocity(performanceMetrics?.overhand_velocity);
  if (variable === 'athlete.gripStrength') return formatStrength(performanceMetrics?.grip_strength);

  // YouTube Channel
  if (variable === 'athlete.youtubeChannelUrl') return athlete?.youtube_channel_url || '';
  if (variable === 'athlete.youtubeChannelHandle') return athlete?.youtube_channel_handle || '';

  // Profile Share Link
  if (variable === 'athlete.profileShareLink') return data.profileShareUrl || '';

  // Highlight video (APS-259)
  if (variable === 'athlete.featuredVideo') return athlete?.featured_video_url || '';

  // The athlete's games at the event this message is about. Resolves to empty
  // when the composer carries no event context (or the athlete has no games
  // there), which leaves the caller's send-time strip to remove the tag and any
  // label line left standing above it — same contract as every other tag whose
  // data the athlete has not filled in.
  if (variable === 'athlete.tournamentSchedule') return formatEventSchedule(data.eventSchedule);

  // Staff-specific variables
  if (variable === 'staff.name') return staffData?.name || '';
  if (variable === 'staff.orgName') return staffData?.orgName || '';
  if (variable === 'staff.teamName') return staffData?.teamName || '';
  if (variable === 'athlete.profileLink') {
    return data.profileShareUrl || '';
  }
  if (variable === 'athlete.youtubeLink') return athlete?.youtube_channel_url || '';
  if (variable === 'athlete.highlight') {
    const name = `${athlete?.firstName || ''} ${athlete?.lastName || ''}`.trim();
    const pos = getPosition(sportInfo?.positions, 0) || athlete?.position || '';
    const gradYear = athlete?.graduationYear ? `Class of ${athlete.graduationYear}` : '';
    const gpa = academicData?.currentGpa ? `GPA: ${academicData.currentGpa}` : '';
    return [name, pos, gradYear, gpa].filter(Boolean).join(' | ');
  }

  // If variable not found, return placeholder as-is
  return placeholder;
};

/**
 * Resolve all variables in a template string
 * 
 * @param template - Email template with {{variable}} placeholders
 * @param data - All data needed for resolution
 * @returns Resolved email text with all placeholders replaced
 */
export const resolveEmailVariables = (template: string, data: ResolveData): string => {
  let result = template;

  // Find all {{variable}} patterns and replace them
  const variablePattern = /\{\{[^}]+\}\}/g;
  const matches = template.match(variablePattern);

  if (matches) {
    matches.forEach(match => {
      const resolvedValue = resolveVariable(match, data);
      // Escape special regex characters in the match
      const escapedMatch = match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedMatch, 'g');
      result = result.replace(regex, resolvedValue);
    });
  }

  return result;
};
